import { useState, useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { PM_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE_URL = `${PM_SERVICE_BASE_URL}/api/v1/projects`;

export default function useTaskTransfer(page = 0, pageSize = 50) {
  const authToken = sessionStorage.getItem("authToken");
  const [transferHistory, setTransferHistory] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";
    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message || "Something went wrong.";
    }
    toast.error(message);
    console.error(`${action} error:`, err);
  };

  // transfer task
  const taskTransfer = useCallback(
    async (projectId, taskId, payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(
          `${BASE_URL}/${projectId}/tasks/${taskId}/transfer`,
          payload,
        );
        // setMessageTemplates((prev) => [res.data, ...prev]);
        toast.success("Task moved to another iteration.");
      } catch (error) {
        handleAxiosErrors(error, "transfer task");
      }
    },
    [authToken],
  );

  // bulk transfer of task
  const bulkTaskTransfer = useCallback(
    async (projectId, payload) => {
      if (!authToken) return;
      try {
        const res = await apiClient.post(
          `${BASE_URL}/${projectId}/tasks/bulk-transfer`,
          payload,
        );
        toast.success("Tasks transfered successfully");
      } catch (error) {
        handleAxiosErrors(error, "bulk task transfer");
      }
    },
    [authToken],
  );

  // fetch task transfer history
  const fetchTaskTransferHistory = useCallback(
    async (projectId, taskId) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.get(
          `${BASE_URL}/${projectId}/tasks/${taskId}/transfer-history`,
        );
        setTransferHistory(res.data.content || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalElements(res.data.totalElements || 0);
      } catch (error) {
        handleAxiosErrors(error, "fetch task transfer history");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken, page, pageSize],
  );

  // fetch project transfer history
  const fetchProjectTransferHistory = useCallback(
    async (projectId) => {
      if (!authToken) return;
      setLoading(true);
      try {
        const res = await apiClient.get(
          `${BASE_URL}/${projectId}/tasks/transfer-history`,
        );
        setTransferHistory(res.data.content || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalElements(res.data.totalElements || 0);
      } catch (error) {
        handleAxiosErrors(error, "fetch project task transfer history");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken, page, pageSize],
  );

  return {
    transferHistory,
    loading,
    initialLoad,
    taskTransfer,
    bulkTaskTransfer,
    fetchTaskTransferHistory,
    fetchProjectTransferHistory,
    totalPages,
    totalElements,
  };
}
